// The Invite a Director/Payment Approver card's choices. Its workflow hands the card the company's payment approver as the
// PAYMENT_APPROVER input when it already has one (a first user who claimed it), and the card then asks only for a director.
// Pure, so the unit tests import it.

export const PAYMENT_APPROVER_INPUT = 'PAYMENT_APPROVER';

export interface InviteFormValues {
  directorFirstName: string;
  directorLastName: string;
  directorJobTitle: string;
  directorEmail: string;
  paymentApproverFirstName: string;
  paymentApproverLastName: string;
  paymentApproverJobTitle: string;
  paymentApproverEmail: string;
  sameUser: boolean;
}

type InputValues = Readonly<Record<string, unknown>> | null | undefined;

// Whether the workflow already has its payment approver, so the card leaves the payment approver out
export function paymentApproverKnown(inputValues: InputValues): boolean {
  const value = inputValues?.[PAYMENT_APPROVER_INPUT];
  return typeof value === 'string' && value.trim() !== '';
}

// Whether the card invites a payment approver of their own: not when the workflow has one, nor when the director is also it
export function invitesPaymentApprover(known: boolean, directorIsPaymentApprover: boolean): boolean {
  return !known && !directorIsPaymentApprover;
}

const DIRECTOR_FIELDS: [keyof InviteFormValues, string][] = [
  ['directorFirstName', 'Director First Name'],
  ['directorLastName', 'Director Last Name'],
  ['directorJobTitle', 'Director Job Title'],
  ['directorEmail', 'Director Email'],
];

const PAYMENT_APPROVER_FIELDS: [keyof InviteFormValues, string][] = [
  ['paymentApproverFirstName', 'Payment Approver First Name'],
  ['paymentApproverLastName', 'Payment Approver Last Name'],
  ['paymentApproverJobTitle', 'Payment Approver Job Title'],
  ['paymentApproverEmail', 'Payment Approver Email'],
];

// The director's details are always required; the payment approver's only when the card invites one
export function inviteFormErrors(
  values: Partial<InviteFormValues>,
  invitePaymentApprover: boolean,
): Partial<Record<keyof InviteFormValues, string>> {
  const errors: Partial<Record<keyof InviteFormValues, string>> = {};
  const fields = invitePaymentApprover ? [...DIRECTOR_FIELDS, ...PAYMENT_APPROVER_FIELDS] : DIRECTOR_FIELDS;
  fields.forEach(([field, label]) => {
    if (!values[field]) errors[field] = `${label} is required`;
  });
  return errors;
}

// The workflow metadata the card writes. With the payment approver known there is no payment approver invitation: the
// workflow waits only for the director. Otherwise the payment approver's invitation is their own or the director's.
export function invitationMetadata(
  known: boolean,
  directorInvitationId: string,
  paymentApproverInvitationId: string | null,
): [string, string][] {
  const metadata: [string, string][] = [['DIRECTOR_INVITATION_ID', directorInvitationId]];
  if (!known) metadata.push(['PAYMENT_APPROVER_INVITATION_ID', paymentApproverInvitationId ?? directorInvitationId]);
  return metadata;
}
